import type { BehaviorDocument } from "../types";
import { createNodeId } from "../types";

export const PICK_PLACE_TEMPLATE_ID = "builtin_pick_place_v1";

// Approach offsets, picked to roughly match pickPlaceServo's hoverHeight (0.12) /
// approachHeight (0.03) stages so the gates fire at the right moments.
// Scene is Y-up; the IK controller converts to Z-up internally for solving.
const APPROACH_Y = 0.15; // "hover" above pick/drop body (scene Y-up meters)
const DESCEND_DY = -0.11; // from APPROACH_Y down to ~0.04 over object center
const LIFT_DY = 0.14; // retreat up after grasp
const RETREAT_DY = 0.12; // retreat up after release

const APPROACH_TOL_M = 0.03; // gate: EE within 3cm of approach point (similar to IK xyTolApproach + slack)
const GRASP_TOL_M = 0.035; // gate: EE within ~3.5cm of grasp point before closing
const GRASP_MIN_HEIGHT_Y = 0.03; // cube must be lifted off the floor before we count it as grasped

export function createPickPlaceTemplate(): BehaviorDocument {
  const nRoot = createNodeId();
  const nRand = createNodeId();
  const nOpenPre = createNodeId();
  const nApproachPick = createNodeId();
  const nGateAbovePick = createNodeId();
  const nDescendPick = createNodeId();
  const nGateAtPick = createNodeId();
  const nClose = createNodeId();
  const nLift = createNodeId();
  const nGateCarry = createNodeId();
  const nApproachDrop = createNodeId();
  const nGateAboveDrop = createNodeId();
  const nDescendDrop = createNodeId();
  const nGatePlace = createNodeId();
  const nOpenDrop = createNodeId();
  const nRetreat = createNodeId();
  const nMark = createNodeId();
  return {
    id: PICK_PLACE_TEMPLATE_ID,
    name: "Pick & Place (template)",
    isTemplate: true,
    baselineSnapshot: null,
    rootId: nRoot,
    nodes: {
      [nRoot]: {
        id: nRoot,
        kind: "sequence",
        label: "Pick & place flow",
        children: [
          nRand,
          nOpenPre,
          nApproachPick,
          nGateAbovePick,
          nDescendPick,
          nGateAtPick,
          nClose,
          nLift,
          nGateCarry,
          nApproachDrop,
          nGateAboveDrop,
          nDescendDrop,
          nGatePlace,
          nOpenDrop,
          nRetreat,
          nMark,
        ],
      },
      [nRand]: {
        id: nRand,
        kind: "randomizeObjectPose",
        label: "Randomize target object",
        bodyUuid: "",
        radiusXY: 0.12,
        yawRangeDeg: 45,
      },
      [nOpenPre]: {
        id: nOpenPre,
        kind: "openGripper",
        label: "Open gripper",
        settleSeconds: 0.4,
      },
      [nApproachPick]: {
        id: nApproachPick,
        kind: "moveEeToXyz",
        label: "Approach pick point",
        x: 0,
        y: APPROACH_Y,
        z: 0,
        targetBodyUuid: "",
        settleSeconds: 0.45,
        orientationMode: "alignWithGrasp",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nGateAbovePick]: {
        id: nGateAbovePick,
        kind: "conditionEeNear",
        label: "Gate: EE hovering above pick",
        endEffectorRef: "gripper_target",
        targetBodyUuid: "",
        x: 0,
        y: APPROACH_Y,
        z: 0,
        toleranceM: APPROACH_TOL_M,
      },
      [nDescendPick]: {
        id: nDescendPick,
        kind: "moveEeByOffset",
        label: "Descend to grasp",
        dx: 0,
        dy: DESCEND_DY,
        dz: 0,
        settleSeconds: 0.35,
        orientationMode: "alignWithGrasp",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nGateAtPick]: {
        id: nGateAtPick,
        kind: "conditionEeNear",
        label: "Gate: EE at grasp point",
        endEffectorRef: "gripper_target",
        targetBodyUuid: "",
        x: 0,
        y: APPROACH_Y + DESCEND_DY,
        z: 0,
        toleranceM: GRASP_TOL_M,
      },
      [nClose]: {
        id: nClose,
        kind: "closeGripper",
        label: "Close gripper",
        settleSeconds: 0.6,
      },
      [nLift]: {
        id: nLift,
        kind: "moveEeByOffset",
        label: "Lift object",
        dx: 0,
        dy: LIFT_DY,
        dz: 0,
        settleSeconds: 0.35,
        orientationMode: "keepDown",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nGateCarry]: {
        id: nGateCarry,
        kind: "conditionObjectGrasped",
        label: "Gate: Object grasped before carry",
        bodyUuid: "",
        minHeightY: GRASP_MIN_HEIGHT_Y,
      },
      [nApproachDrop]: {
        id: nApproachDrop,
        kind: "moveEeToXyz",
        label: "Approach drop point",
        x: 0,
        y: APPROACH_Y,
        z: 0,
        targetBodyUuid: "",
        settleSeconds: 0.45,
        orientationMode: "keepDown",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nGateAboveDrop]: {
        id: nGateAboveDrop,
        kind: "conditionEeNear",
        label: "Gate: EE hovering above drop",
        endEffectorRef: "gripper_target",
        targetBodyUuid: "",
        x: 0,
        y: APPROACH_Y,
        z: 0,
        toleranceM: APPROACH_TOL_M,
      },
      [nDescendDrop]: {
        id: nDescendDrop,
        kind: "moveEeByOffset",
        label: "Descend to place",
        dx: 0,
        dy: DESCEND_DY,
        dz: 0,
        settleSeconds: 0.35,
        orientationMode: "keepDown",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nGatePlace]: {
        id: nGatePlace,
        kind: "conditionObjectGrasped",
        label: "Gate: Object grasped before place",
        bodyUuid: "",
        minHeightY: GRASP_MIN_HEIGHT_Y,
      },
      [nOpenDrop]: {
        id: nOpenDrop,
        kind: "openGripper",
        label: "Release object",
        settleSeconds: 0.5,
      },
      [nRetreat]: {
        id: nRetreat,
        kind: "moveEeByOffset",
        label: "Retreat up",
        dx: 0,
        dy: RETREAT_DY,
        dz: 0,
        settleSeconds: 0.35,
        orientationMode: "keepDown",
        orientationStrength: 0.8,
        endEffectorRef: "gripper_target",
      },
      [nMark]: { id: nMark, kind: "markSuccess", label: "Mark success" },
    },
  };
}

export const BUILTIN_TEMPLATE_IDS = [PICK_PLACE_TEMPLATE_ID] as const;
